<template>
  <v-card flat>
    <v-card-text>
      <v-container>
        <!-- Скелетон для информации об устройстве -->
        <v-row v-if="loadingDetails && !deviceData">
          <v-col cols="12">
            <h3 class="mb-3">
              <v-icon left>mdi-information-outline</v-icon>
              {{ $t('starline.device_info') }}
            </h3>
            <v-row>
              <v-col v-for="i in 8" :key="'skeleton-info-' + i" cols="6" sm="4" md="3" lg="2">
                <v-skeleton-loader type="list-item-two-line" class="mx-0"></v-skeleton-loader>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Информация об устройстве -->
        <v-row v-if="deviceData">
          <v-col cols="12">
            <h3 class="mb-3">
              <v-icon left>mdi-information-outline</v-icon>
              {{ $t('starline.device_info') }}
              <v-progress-circular v-if="loadingDetails" indeterminate size="16" width="2" class="ml-2"></v-progress-circular>
            </h3>
            <v-row>
              <v-col 
                v-for="item in deviceInfoItems" 
                :key="item.label" 
                cols="6" 
                sm="4" 
                md="3" 
                lg="2"
              >
                <v-list-item dense class="px-0">
                  <v-list-item-icon class="mr-2"><v-icon small :color="item.color || ''">{{ item.icon }}</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle class="text-caption">{{ item.label }}</v-list-item-subtitle>
                    <v-list-item-title :class="item.textColor ? item.textColor + '--text' : ''">{{ item.value }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
            
            <!-- Баланс SIM -->
            <v-row v-if="simBalance">
              <v-col cols="12">
                <v-alert 
                  :type="simBalance.value <= 0 ? 'error' : (simBalance.state === 1 ? 'warning' : 'info')" 
                  dense 
                  outlined
                  class="mb-0"
                >
                  <v-row align="center" no-gutters>
                    <v-col class="grow">
                      <strong>{{ $t('starline.sim_balance') }}:</strong> 
                      {{ simBalance.value }} {{ simBalance.currency }}
                      <span class="ml-2 grey--text">{{ simBalance.operator }}</span>
                      <span v-if="simBalance.number" class="ml-2 grey--text">({{ simBalance.number }})</span>
                    </v-col>
                    <v-col v-if="simBalance.url_payment" class="shrink">
                      <v-btn
                        small
                        color="success"
                        :href="simBalance.url_payment"
                        target="_blank"
                      >
                        <v-icon left small>mdi-credit-card</v-icon>
                        {{ $t('starline.top_up_balance') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-alert>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4" v-if="deviceData || loadingDetails"></v-divider>

        <!-- Скелетон для состояния автомобиля -->
        <v-row v-if="loadingDetails && !deviceData">
          <v-col cols="12">
            <h3 class="mb-3">
              <v-icon left>mdi-car-info</v-icon>
              {{ $t('starline.device_status') }}
            </h3>
            <v-row>
              <v-col v-for="i in 6" :key="'skeleton-status-' + i" cols="6" sm="4" md="2" class="text-center">
                <v-skeleton-loader type="chip" class="mx-auto"></v-skeleton-loader>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Состояние автомобиля -->
        <v-row v-if="deviceData">
          <v-col cols="12">
            <h3 class="mb-3">
              <v-icon left>mdi-car-info</v-icon>
              {{ $t('starline.device_status') }}
            </h3>
            <v-row>
              <v-col cols="6" sm="4" md="2" class="text-center">
                <v-chip :color="carState?.arm ? 'success' : 'grey'" dark small>
                  <v-icon left small>mdi-shield-car</v-icon>
                  {{ carState?.arm ? $t('starline.arm_on') : $t('starline.arm_off') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center">
                <v-chip :color="carState?.ign ? 'warning' : 'grey'" dark small>
                  <v-icon left small>mdi-engine</v-icon>
                  {{ carState?.ign ? $t('starline.engine_on') : $t('starline.engine_off') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center">
                <v-chip :color="carState?.door ? 'error' : 'success'" dark small>
                  <v-icon left small>mdi-car-door</v-icon>
                  {{ carState?.door ? $t('starline.doors_open') : $t('starline.doors_closed') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center">
                <v-chip :color="carState?.hood ? 'error' : 'success'" dark small>
                  <v-icon left small>mdi-car</v-icon>
                  {{ carState?.hood ? $t('starline.hood_open') : $t('starline.hood_closed') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center">
                <v-chip :color="carState?.trunk ? 'error' : 'success'" dark small>
                  <v-icon left small>mdi-car-back</v-icon>
                  {{ carState?.trunk ? $t('starline.trunk_open') : $t('starline.trunk_closed') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center">
                <v-chip :color="carState?.valet ? 'info' : 'grey'" dark small>
                  <v-icon left small>mdi-key-variant</v-icon>
                  {{ carState?.valet ? $t('starline.valet_on') : $t('starline.valet_off') }}
                </v-chip>
              </v-col>
            </v-row>
            <!-- Дополнительные состояния -->
            <v-row class="mt-2">
              <v-col cols="6" sm="4" md="2" class="text-center">
                <v-chip :color="carState?.pbrake ? 'info' : 'grey'" dark small>
                  <v-icon left small>mdi-car-brake-parking</v-icon>
                  {{ $t('starline.parking_brake') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="carState?.hfree">
                <v-chip color="info" dark small>
                  <v-icon left small>mdi-hand-back-right-off</v-icon>
                  {{ $t('starline.hands_free') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="carState?.ign">
                <v-chip color="warning" dark small>
                  <v-icon left small>mdi-engine</v-icon>
                  {{ $t('starline.engine_running') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="carState?.r_start">
                <v-chip color="info" dark small>
                  <v-icon left small>mdi-remote</v-icon>
                  {{ $t('starline.remote_start') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="carState?.relay">
                <v-chip color="error" dark small>
                  <v-icon left small>mdi-lock</v-icon>
                  {{ $t('starline.engine_blocked') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="carState?.webasto !== undefined">
                <v-chip :color="carState?.webasto ? 'warning' : 'grey'" dark small>
                  <v-icon left small>mdi-thermometer-plus</v-icon>
                  {{ carState?.webasto ? $t('starline.webasto_on') : $t('starline.webasto_off') }}
                </v-chip>
              </v-col>
              <v-col cols="6" sm="4" md="2" class="text-center" v-if="carState?.neutral">
                <v-chip color="info" dark small>
                  <v-icon left small>mdi-alpha-n-circle</v-icon>
                  {{ $t('starline.neutral_gear') }}
                </v-chip>
              </v-col>
            </v-row>
            
            <!-- Состояния тревоги (alarm_state) -->
            <v-row class="mt-2" v-if="hasAlarmTriggers">
              <v-col cols="12">
                <v-alert type="error" dense outlined class="mb-0">
                  <v-icon left small>mdi-alarm-light</v-icon>
                  <strong>{{ $t('starline.alarm_triggers') }}:</strong>
                  <v-chip v-if="alarmState?.shock_h || alarmState?.shock_l" color="error" dark x-small class="ml-2">
                    {{ $t('starline.shock_sensor') }}
                  </v-chip>
                  <v-chip v-if="alarmState?.tilt" color="error" dark x-small class="ml-2">
                    {{ $t('starline.tilt_sensor') }}
                  </v-chip>
                  <v-chip v-if="alarmState?.door" color="error" dark x-small class="ml-2">
                    {{ $t('starline.door_sensor') }}
                  </v-chip>
                  <v-chip v-if="alarmState?.hood" color="error" dark x-small class="ml-2">
                    {{ $t('starline.hood_sensor') }}
                  </v-chip>
                  <v-chip v-if="alarmState?.trunk" color="error" dark x-small class="ml-2">
                    {{ $t('starline.trunk_sensor') }}
                  </v-chip>
                  <v-chip v-if="alarmState?.hijack" color="error" dark x-small class="ml-2">
                    {{ $t('starline.hijack_mode') }}
                  </v-chip>
                  <v-chip v-if="alarmState?.move" color="error" dark x-small class="ml-2">
                    {{ $t('starline.move_sensor') }}
                  </v-chip>
                  <v-chip v-if="carState?.alarm" color="error" dark x-small class="ml-2">
                    {{ $t('starline.alarm_active') }}
                  </v-chip>
                  <v-chip v-if="carState?.hijack" color="error" dark x-small class="ml-2">
                    {{ $t('starline.hijack_mode') }}
                  </v-chip>
                </v-alert>
              </v-col>
            </v-row>
            
            <!-- Последняя активность -->
            <v-row class="mt-3" v-if="deviceData.activity_ts || deviceData.ts_activity">
              <v-col cols="12">
                <span class="grey--text text--darken-1">
                  <v-icon small class="mr-1">mdi-clock-outline</v-icon>
                  {{ $t('starline.last_activity') }}: {{ formatDateTime(deviceData.activity_ts || deviceData.ts_activity) }}
                </span>
              </v-col>
            </v-row>
            
            <!-- Debug: Raw JSON -->
            <v-row class="mt-3">
              <v-col cols="12">
                <v-expansion-panels flat>
                  <v-expansion-panel>
                    <v-expansion-panel-header class="grey--text">
                      <v-icon small class="mr-2">mdi-code-json</v-icon>
                      Raw JSON
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <pre 
                        :class="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                        style="max-height: 400px; overflow: auto; padding: 10px; font-size: 11px; border-radius: 4px;"
                      >{{ JSON.stringify(deviceData, null, 2) }}</pre>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Геолокация -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">
              {{ $t('starline.geolocation') }}
              <v-progress-circular v-if="loadingPosition" indeterminate size="16" width="2" class="ml-2"></v-progress-circular>
            </h3>
            <v-row align="center">
              <v-col cols="12" md="8">
                <!-- Скелетон для позиции -->
                <v-skeleton-loader v-if="loadingPosition && !position" type="text" class="my-2"></v-skeleton-loader>
                
                <div v-else-if="position">
                  <p>
                    <strong>{{ $t('starline.coordinates') }}:</strong> 
                    {{ position.lat }}, {{ position.lon }}
                    <span v-if="positionUpdatedAt" class="ml-2 grey--text text--darken-1">
                      ({{ $t('starline.updated') }}: {{ formatTime(positionUpdatedAt) }})
                    </span>
                  </p>
                </div>
                <div v-else>
                  <p class="grey--text">{{ $t('starline.no_position_data') }}</p>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="text-right">
                <v-btn color="primary" small @click="openMap" :disabled="!position" class="mr-2">
                  <v-icon left small>mdi-map-marker</v-icon>
                  {{ $t('starline.open_on_map') }}
                </v-btn>
                <v-btn color="secondary" small @click="refreshPosition" :loading="loadingPosition">
                  <v-icon left small>mdi-refresh</v-icon>
                  {{ $t('starline.refresh') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Управление -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('starline.controls') }}</h3>
            
            <!-- Нет доступных команд (после загрузки) -->
            <v-row v-if="controlsLoaded && availableControls.length === 0">
              <v-col cols="12">
                <v-alert type="info" dense outlined>
                  {{ $t('starline.no_commands_available') }}
                </v-alert>
              </v-col>
            </v-row>
            
            <!-- Выбор команды с ленивой загрузкой -->
            <v-row v-else align="center">
              <v-col cols="12" md="6" lg="5">
                <v-autocomplete
                  v-model="selectedCommand"
                  :items="availableControls"
                  item-text="title"
                  item-value="type"
                  :label="$t('starline.select_command')"
                  :placeholder="$t('starline.select_command_placeholder')"
                  :hint="selectedCommand ? getCommandJson(selectedCommand) : ''"
                  persistent-hint
                  outlined
                  dense
                  clearable
                  :disabled="executingCommand !== null"
                  :loading="loadingControls"
                  return-object
                  @click="onControlsFocus"
                >
                  <template v-slot:item="{ item }">
                    <span>{{ getControlLabel(item) }}</span>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span>{{ getControlLabel(item) }}</span>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="3" lg="2">
                <v-btn
                  color="primary"
                  :loading="executingCommand !== null"
                  :disabled="!selectedCommand || executingCommand !== null"
                  @click="sendSelectedCommand"
                  block
                  class="mb-6"
                >
                  <v-icon left>mdi-send</v-icon>
                  {{ $t('starline.send_command') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Пробег OBD -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('starline.obd_mileage') }}</h3>
            <v-row align="center">
              <v-col cols="12" md="8">
                <p>
                  <strong>{{ $t('starline.system_mileage') }}:</strong> {{ car.mileage || 0 }} {{ $t('custom.km') }}
                </p>
                <!-- Сообщение когда OBD недоступен -->
                <v-alert 
                  v-if="obdChecked && !obdAvailable" 
                  type="warning" 
                  dense 
                  outlined 
                  class="mt-2"
                >
                  {{ obdMessage || $t('starline.obd_unavailable') }}
                </v-alert>
                <p v-if="obdMileage !== null">
                  <strong>{{ $t('starline.obd_mileage_value') }}:</strong> {{ obdMileage }} {{ $t('custom.km') }}
                  <span v-if="mileageDifference !== null" 
                        :class="Math.abs(mileageDifference) > mileageThreshold ? 'red--text' : 'green--text'">
                    ({{ mileageDifference > 0 ? '+' : '' }}{{ mileageDifference }} {{ $t('custom.km') }})
                  </span>
                </p>
                
              </v-col>
              <v-col cols="12" md="4" class="text-right">
                <v-btn color="info" small @click="fetchMileage" :loading="loadingMileage" class="mr-2">
                  <v-icon left small>mdi-refresh</v-icon>
                  {{ $t('starline.check_mileage') }}
                </v-btn>
                <v-btn 
                  color="success" 
                  small 
                  @click="syncMileage" 
                  :loading="syncingMileage"
                  :disabled="!obdAvailable || obdMileage === null || Math.abs(mileageDifference) > mileageThreshold"
                >
                  <v-icon left small>mdi-download</v-icon>
                  {{ $t('starline.save_from_device') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- История треков -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('starline.track_history') }}</h3>
            <v-row align="center">
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="trackStartMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(trackStartDate, trackStartTime)"
                      :label="$t('starline.start_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="trackStartTab" fixed-tabs>
                      <v-tab>{{ $t('starline.date') }}</v-tab>
                      <v-tab>{{ $t('starline.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="trackStartTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="trackStartDate"
                          :max="today"
                          @input="trackStartTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="trackStartTime"
                          format="24hr"
                          @click:minute="trackStartMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="trackEndMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(trackEndDate, trackEndTime)"
                      :label="$t('starline.end_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="trackEndTab" fixed-tabs>
                      <v-tab>{{ $t('starline.date') }}</v-tab>
                      <v-tab>{{ $t('starline.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="trackEndTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="trackEndDate"
                          :max="today"
                          @input="trackEndTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="trackEndTime"
                          format="24hr"
                          @click:minute="trackEndMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="2">
                <v-btn color="primary" small @click="fetchWays" :loading="loadingWays">
                  {{ $t('starline.show') }}
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-if="waysData">
              <v-col cols="12">
                <p>
                  <strong>{{ $t('starline.track_mileage') }}:</strong> {{ waysData.mileage || 0 }} {{ $t('custom.km') }} |
                  <strong>{{ $t('starline.travel_time') }}:</strong> {{ formatDuration(waysData.moving_time) }}
                </p>
                <v-btn color="info" small @click="openTrackOnMap" :disabled="!waysData.way || waysData.way.length === 0">
                  <v-icon left small>mdi-map</v-icon>
                  {{ $t('starline.open_track_on_map') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- История событий -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('starline.event_history') }}</h3>
            <v-row align="center">
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="eventStartMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(eventStartDate, eventStartTime)"
                      :label="$t('starline.start_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="eventStartTab" fixed-tabs>
                      <v-tab>{{ $t('starline.date') }}</v-tab>
                      <v-tab>{{ $t('starline.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="eventStartTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="eventStartDate"
                          :max="today"
                          @input="eventStartTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="eventStartTime"
                          format="24hr"
                          @click:minute="eventStartMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="eventEndMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(eventEndDate, eventEndTime)"
                      :label="$t('starline.end_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="eventEndTab" fixed-tabs>
                      <v-tab>{{ $t('starline.date') }}</v-tab>
                      <v-tab>{{ $t('starline.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="eventEndTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="eventEndDate"
                          :max="today"
                          @input="eventEndTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="eventEndTime"
                          format="24hr"
                          @click:minute="eventEndMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="2">
                <v-btn color="primary" small @click="fetchEvents" :loading="loadingEvents">
                  {{ $t('starline.show') }}
                </v-btn>
              </v-col>
            </v-row>
            <v-simple-table v-if="events && events.length > 0" class="mt-3">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>{{ $t('starline.event_time') }}</th>
                    <th>{{ $t('starline.event_type') }}</th>
                    <th>{{ $t('starline.event_description') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(event, index) in events" :key="index">
                    <td>{{ formatDateTime(event.ts) }}</td>
                    <td>{{ event.name || getEventLabel(event.type) }}</td>
                    <td>{{ event.description || '-' }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <p v-else-if="eventsLoaded" class="grey--text mt-3">{{ $t('starline.no_events') }}</p>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Scoring водителя -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('starline.driving_score') }}</h3>
            <v-row align="center">
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="scoreStartMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(scoreStartDate, scoreStartTime)"
                      :label="$t('starline.start_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="scoreStartTab" fixed-tabs>
                      <v-tab>{{ $t('starline.date') }}</v-tab>
                      <v-tab>{{ $t('starline.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="scoreStartTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="scoreStartDate"
                          :max="today"
                          @input="scoreStartTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="scoreStartTime"
                          format="24hr"
                          @click:minute="scoreStartMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="scoreEndMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(scoreEndDate, scoreEndTime)"
                      :label="$t('starline.end_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="scoreEndTab" fixed-tabs>
                      <v-tab>{{ $t('starline.date') }}</v-tab>
                      <v-tab>{{ $t('starline.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="scoreEndTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="scoreEndDate"
                          :max="today"
                          @input="scoreEndTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="scoreEndTime"
                          format="24hr"
                          @click:minute="scoreEndMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="2">
                <v-btn color="primary" small @click="fetchDrivingScore" :loading="loadingScore">
                  {{ $t('starline.show') }}
                </v-btn>
              </v-col>
            </v-row>
            <v-simple-table v-if="drivingScoreData && drivingScoreData.length > 0" class="mt-3">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>{{ $t('starline.date') }}</th>
                    <th>{{ $t('starline.score') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(score, index) in drivingScoreData" :key="index">
                    <td>{{ formatDate(score.date) }}</td>
                    <td>
                      <v-chip :color="getScoreColor(score.value)" dark small>
                        {{ score.value }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <p v-else-if="scoreLoaded" class="grey--text mt-3">{{ $t('starline.no_score_data') }}</p>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- OBD ошибки -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('starline.obd_errors') }}</h3>
            <v-btn color="primary" small @click="fetchObdErrors" :loading="loadingObdErrors" class="mb-3">
              {{ $t('starline.check_obd_errors') }}
            </v-btn>
            
            <v-alert v-if="obdErrorsChecked && obdErrors.length === 0" type="success" dense outlined class="mt-3">
              {{ $t('starline.no_obd_errors') }}
            </v-alert>
            
            <v-simple-table v-if="obdErrors && obdErrors.length > 0" class="mt-3">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>{{ $t('starline.error_code') }}</th>
                    <th>{{ $t('starline.error_description') }}</th>
                    <th>{{ $t('starline.error_date') }}</th>
                    <th>{{ $t('starline.warning_level') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(error, index) in obdErrors" :key="index">
                    <td>
                      <v-chip color="error" dark small>{{ error.error }}</v-chip>
                    </td>
                    <td>{{ getObdErrorDescription(error) }}</td>
                    <td>{{ formatDateTime(error.error_ts) }}</td>
                    <td>
                      <v-chip :color="getWarningLevelColor(error.warning_level)" dark small>
                        {{ error.warning_level }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <!-- Snackbar для уведомлений -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          {{ $t('custom.close') }}
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Диалог с картой трека -->
    <v-dialog v-model="trackMapDialog" max-width="900" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>{{ $t('starline.track_on_map') }}</span>
          <v-btn icon @click="trackMapDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <GmapMap
            v-if="trackMapDialog && trackPath.length > 0"
            :center="trackCenter"
            :zoom="trackZoom"
            style="width: 100%; height: 500px;"
            :options="{
              zoomControl: true,
              mapTypeControl: true,
              scaleControl: true,
              streetViewControl: false,
              rotateControl: false,
              fullscreenControl: true
            }"
          >
            <!-- Линия трека -->
            <GmapPolyline
              :path="trackPath"
              :options="{
                strokeColor: '#1976D2',
                strokeOpacity: 0.9,
                strokeWeight: 4
              }"
            />
            <!-- Маркер начала -->
            <GmapMarker
              :position="trackPath[0]"
              :icon="{
                url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
              }"
              :title="$t('starline.track_start')"
            />
            <!-- Маркер конца -->
            <GmapMarker
              :position="trackPath[trackPath.length - 1]"
              :icon="{
                url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'
              }"
              :title="$t('starline.track_end')"
            />
            <!-- Маркеры событий -->
            <GmapMarker
              v-for="(event, index) in trackEvents"
              :key="'event-' + index"
              :position="event.position"
              :icon="getEventIcon(event.type)"
              :title="event.title"
              @click="selectedEvent = event"
            />
            <!-- Инфо-окно для выбранного события -->
            <GmapInfoWindow
              v-if="selectedEvent"
              :position="selectedEvent.position"
              :opened="!!selectedEvent"
              @closeclick="selectedEvent = null"
            >
              <div class="pa-2">
                <strong>{{ selectedEvent.title }}</strong>
                <div class="mt-1 text-caption">{{ selectedEvent.time }}</div>
                <div v-if="selectedEvent.text" class="mt-1">{{ selectedEvent.text }}</div>
              </div>
            </GmapInfoWindow>
          </GmapMap>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="trackMapDialog = false">
            {{ $t('custom.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import CableSubscriber from "@/mixins/CableSubscriber";
export default {
  name: "StarlineTab",
  mixins: [CableSubscriber],
  props: {
    car: {
      type: Object,
      required: true
    },
    company: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // Loading states
      loadingDetails: true,
      
      // Device state
      deviceData: null,
      position: null,
      positionUpdatedAt: null,
      
      // Controls
      availableControls: [],
      selectedCommand: null,
      executingCommand: null,
      loadingControls: false,
      controlsLoaded: false,
      
      // Mileage
      obdMileage: null,
      mileageDifference: null,
      obdAvailable: true,
      obdMessage: null,
      obdChecked: false,
      loadingMileage: false,
      syncingMileage: false,
      
      // Position
      loadingPosition: true,
      
      // Track history
      trackStartDate: this.getYesterday(),
      trackStartTime: '00:00',
      trackEndDate: this.getToday(),
      trackEndTime: '23:59',
      trackStartMenu: false,
      trackEndMenu: false,
      trackStartTab: 0,
      trackEndTab: 0,
      waysData: null,
      loadingWays: false,
      
      // Track map dialog
      trackMapDialog: false,
      trackPath: [],
      trackCenter: { lat: 55.7558, lng: 37.6173 },
      trackZoom: 12,
      trackEvents: [],
      selectedEvent: null,
      
      // Events
      eventStartDate: this.getYesterday(),
      eventStartTime: '00:00',
      eventEndDate: this.getToday(),
      eventEndTime: '23:59',
      eventStartMenu: false,
      eventStartTab: 0,
      eventEndMenu: false,
      eventEndTab: 0,
      events: [],
      eventsLoaded: false,
      loadingEvents: false,
      
      // Driving score
      scoreStartDate: this.getYesterday(),
      scoreStartTime: '00:00',
      scoreEndDate: this.getToday(),
      scoreEndTime: '23:59',
      scoreStartMenu: false,
      scoreEndMenu: false,
      scoreStartTab: 0,
      scoreEndTab: 0,
      drivingScoreData: [],
      scoreLoaded: false,
      loadingScore: false,
      
      // OBD Errors
      obdErrors: [],
      obdErrorsChecked: false,
      loadingObdErrors: false,
      
      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      
      // ActionCable
      subscribedCarId: null,
      pendingCommandId: null,
      lastCommandType: null,
      commandTimeout: null
    };
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    today() {
      return new Date().toISOString().substr(0, 10);
    },
    mileageThreshold() {
      return this.company?.starline_mileage_threshold || 500;
    },
    // Универсальные геттеры для разных структур API
    carState() {
      return this.deviceData?.state || this.deviceData?.car_state;
    },
    alarmState() {
      return this.deviceData?.alarm_state || this.deviceData?.car_alr_state;
    },
    simBalance() {
      // balance может быть массивом или объектом с active
      if (Array.isArray(this.deviceData?.balance) && this.deviceData.balance.length > 0) {
        return this.deviceData.balance.find(b => b.key === 'active') || this.deviceData.balance[0];
      }
      return this.deviceData?.balance?.active;
    },
    deviceBattery() {
      return this.deviceData?.common?.battery ?? this.deviceData?.battery ?? null;
    },
    deviceGsmLvl() {
      return this.deviceData?.common?.gsm_lvl ?? this.deviceData?.gsm_lvl ?? null;
    },
    deviceEtemp() {
      const val = this.deviceData?.common?.etemp ?? this.deviceData?.etemp;
      return val !== null && val !== undefined ? val : null;
    },
    deviceCtemp() {
      const val = this.deviceData?.common?.ctemp;
      return val !== null && val !== undefined ? val : null;
    },
    deviceMotohours() {
      const state = this.deviceData?.state || this.deviceData?.car_state;
      return state?.motohrs ?? null;
    },
    hasAlarmTriggers() {
      const alr = this.alarmState;
      const state = this.carState;
      if (!alr && !state) return false;
      return (alr?.shock_h || alr?.shock_l || alr?.tilt || alr?.door || 
              alr?.hood || alr?.trunk || alr?.hijack || alr?.move ||
              state?.alarm || state?.hijack);
    },
    deviceInfoItems() {
      const items = [];
      const d = this.deviceData;
      if (!d) return items;
      
      // Основная информация
      if (d.alias) {
        items.push({ icon: 'mdi-tag', label: this.$t('starline.device_alias'), value: d.alias });
      }
      if (d.typename) {
        items.push({ icon: 'mdi-devices', label: this.$t('starline.device_type'), value: d.typename });
      }
      if (d.sn) {
        items.push({ icon: 'mdi-barcode', label: this.$t('starline.serial_number'), value: d.sn });
      }
      if (d.firmware_version || d.fw_version) {
        items.push({ icon: 'mdi-chip', label: this.$t('starline.firmware'), value: d.firmware_version || d.fw_version });
      }
      if (d.telephone || d.phone) {
        items.push({ icon: 'mdi-phone', label: this.$t('starline.sim_phone'), value: d.telephone || d.phone });
      }
      if (d.device_id) {
        items.push({ icon: 'mdi-identifier', label: 'Device ID', value: d.device_id });
      }
      if (d.imei) {
        items.push({ icon: 'mdi-cellphone', label: 'IMEI', value: d.imei });
      }
      if (d.registration_plate) {
        items.push({ icon: 'mdi-car', label: this.$t('starline.reg_plate'), value: d.registration_plate });
      }
      
      // Технические данные
      if (this.deviceBattery !== null) {
        const color = this.getBatteryColor(this.deviceBattery);
        items.push({ 
          icon: 'mdi-car-battery', 
          label: this.$t('starline.battery'), 
          value: this.deviceBattery.toFixed(2) + ' V',
          color: color,
          textColor: color
        });
      }
      if (this.deviceGsmLvl !== null) {
        const color = this.getGsmColor(this.deviceGsmLvl);
        items.push({ 
          icon: 'mdi-signal', 
          label: this.$t('starline.gsm_signal'), 
          value: this.deviceGsmLvl + '%',
          color: color
        });
      }
      if (this.deviceCtemp !== null) {
        items.push({ icon: 'mdi-thermometer-low', label: this.$t('starline.cabin_temp'), value: this.deviceCtemp + '°C' });
      }
      if (this.deviceEtemp !== null) {
        items.push({ icon: 'mdi-thermometer', label: this.$t('starline.engine_temp'), value: this.deviceEtemp + '°C' });
      }
      
      // Дополнительные данные
      if (this.deviceMotohours !== null) {
        items.push({ icon: 'mdi-clock-fast', label: this.$t('starline.motohours'), value: this.deviceMotohours + ' ' + this.$t('starline.hours_short') });
      }
      if (d.obd?.mileage !== null && d.obd?.mileage !== undefined) {
        items.push({ icon: 'mdi-counter', label: this.$t('starline.obd_mileage'), value: d.obd.mileage + ' ' + this.$t('custom.km') });
      }
      if (d.position?.sat_qty !== undefined) {
        items.push({ icon: 'mdi-satellite-variant', label: this.$t('starline.satellites'), value: d.position.sat_qty });
      }
      if (d.position?.s !== undefined) {
        items.push({ icon: 'mdi-speedometer', label: this.$t('starline.speed'), value: d.position.s + ' ' + this.$t('custom.kmh') });
      }
      if (d.position?.is_move !== undefined) {
        const isMoving = d.position.is_move;
        items.push({ 
          icon: 'mdi-car-side', 
          label: this.$t('starline.movement'), 
          value: isMoving ? this.$t('starline.moving') : this.$t('starline.stationary'),
          color: isMoving ? 'warning' : 'grey'
        });
      }
      
      return items;
    }
  },
  mounted() {
    this.fetchDeviceDetails();
    this.fetchPosition();
    this.subscribeToChannel();
  },
  beforeDestroy() {
    this.unsubscribeFromChannel();
    if (this.commandTimeout) {
      clearTimeout(this.commandTimeout);
    }
  },
  methods: {
    // Date helpers
    getToday() {
      return new Date().toISOString().substr(0, 10);
    },
    getYesterday() {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday.toISOString().substr(0, 10);
    },
    
    // Formatting
    formatTime(timestamp) {
      if (!timestamp) return '';
      return new Date(timestamp * 1000).toLocaleTimeString();
    },
    formatDate(timestamp) {
      if (!timestamp) return '';
      return new Date(timestamp * 1000).toLocaleDateString();
    },
    formatDateTime(timestamp) {
      if (!timestamp) return '';
      return new Date(timestamp * 1000).toLocaleString();
    },
    
    // Форматирование даты и времени для input поля
    formatDateTimeInput(date, time) {
      if (!date) return '';
      const [year, month, day] = date.split('-');
      return `${day}.${month}.${year} ${time || '00:00'}`;
    },
    
    formatDuration(seconds) {
      if (!seconds) return '0м';
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      if (hours > 0) {
        return `${hours}ч ${minutes}м`;
      }
      return `${minutes}м`;
    },
    
    // API calls
    getAuthHeaders() {
      return {
        headers: {
          Authorization: this.$store.getters.getAuthToken,
        },
      };
    },
    
    async fetchDeviceDetails() {
      this.loadingDetails = true;
      try {
        const response = await this.axios.get(`/api/v1/starline/${this.car.id}/details`, this.getAuthHeaders());
        this.deviceData = response.data.data;
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingDetails = false;
      }
    },
    
    // Обработчик фокуса/клика на поле выбора команды
    onControlsFocus() {
      if (!this.controlsLoaded && !this.loadingControls) {
        this.fetchControlsLibrary();
      }
    },
    
    // Ленивая загрузка списка команд
    async fetchControlsLibrary() {
      this.loadingControls = true;
      try {
        const response = await this.axios.get(`/api/v1/starline/${this.car.id}/controls_library`, this.getAuthHeaders());
        const controlsLibrary = response.data.controls || {};
        
        if (Object.keys(controlsLibrary).length > 0) {
          this.availableControls = this.parseControlsLibrary(controlsLibrary);
        } else {
          this.availableControls = [];
        }
        this.controlsLoaded = true;
      } catch (error) {
        this.showError(error);
        this.controlsLoaded = true; // Помечаем как загруженный чтобы показать "нет команд"
      } finally {
        this.loadingControls = false;
      }
    },
    
    // Парсим controls_library в массив кнопок - ТОЛЬКО команды из API
    parseControlsLibrary(controlsLibrary) {
      const controls = [];
      let position = 0;
      
      // Добавляем ТОЛЬКО команды из controls_library
      Object.keys(controlsLibrary).forEach(type => {
        // Для toggle-команд типа arm/ign добавляем start/stop варианты
        if (type === 'arm') {
          controls.push({ type: 'arm_start', position: position++, title: controlsLibrary[type].title });
          controls.push({ type: 'arm_stop', position: position++, title: controlsLibrary[type].title });
        } else if (type === 'ign') {
          controls.push({ type: 'ign_start', position: position++, title: controlsLibrary[type].title });
          controls.push({ type: 'ign_stop', position: position++, title: controlsLibrary[type].title });
        } else if (type === 'webasto') {
          controls.push({ type: 'webasto_on', position: position++, title: controlsLibrary[type].title });
          controls.push({ type: 'webasto_off', position: position++, title: controlsLibrary[type].title });
        } else {
          controls.push({ type: type, position: position++, title: controlsLibrary[type].title });
        }
      });
      
      return controls;
    },
    
    async fetchPosition() {
      this.loadingPosition = true;
      try {
        const response = await this.axios.get(`/api/v1/starline/${this.car.id}/position`, this.getAuthHeaders());
        this.position = response.data;
        this.positionUpdatedAt = response.data.ts;
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingPosition = false;
      }
    },
    
    async refreshPosition() {
      // Сначала отправляем команду обновления позиции
      await this.executeCommand('update_position', 1);
      // Затем через небольшую задержку получаем новые данные
      setTimeout(() => {
        this.fetchPosition();
      }, 3000);
    },
    
    async fetchMileage() {
      this.loadingMileage = true;
      try {
        const response = await this.axios.get(`/api/v1/starline/${this.car.id}/mileage`, this.getAuthHeaders());
        this.obdMileage = response.data.obd_mileage;
        this.mileageDifference = response.data.difference;
        this.obdAvailable = response.data.obd_available !== false;
        this.obdMessage = response.data.message;
        this.obdChecked = true;
      } catch (error) {
        this.showError(error);
        this.obdChecked = true;
      } finally {
        this.loadingMileage = false;
      }
    },
    
    async syncMileage() {
      this.syncingMileage = true;
      try {
        await this.axios.post(`/api/v1/starline/${this.car.id}/mileage/sync`, {}, this.getAuthHeaders());
        this.showSuccess(this.$t('starline.mileage_synced'));
        this.$emit('mileage-updated');
        this.fetchMileage();
      } catch (error) {
        this.showError(error);
      } finally {
        this.syncingMileage = false;
      }
    },
    
    async fetchWays() {
      this.loadingWays = true;
      try {
        const begin = new Date(`${this.trackStartDate}T${this.trackStartTime}:00`).getTime() / 1000;
        const end = new Date(`${this.trackEndDate}T${this.trackEndTime}:00`).getTime() / 1000;
        const response = await this.axios.get(`/api/v1/starline/${this.car.id}/ways`, {
          params: { begin, end },
          ...this.getAuthHeaders()
        });
        this.waysData = response.data;
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingWays = false;
      }
    },
    
    async fetchEvents() {
      this.loadingEvents = true;
      try {
        const period_start = new Date(`${this.eventStartDate}T${this.eventStartTime}:00`).getTime() / 1000;
        const period_end = new Date(`${this.eventEndDate}T${this.eventEndTime}:00`).getTime() / 1000;
        const response = await this.axios.get(`/api/v1/starline/${this.car.id}/events`, {
          params: { period_start, period_end },
          ...this.getAuthHeaders()
        });
        this.events = response.data.events || [];
        this.eventsLoaded = true;
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingEvents = false;
      }
    },
    
    async fetchDrivingScore() {
      this.loadingScore = true;
      try {
        const period_start = new Date(`${this.scoreStartDate}T${this.scoreStartTime}:00`).getTime() / 1000;
        const period_end = new Date(`${this.scoreEndDate}T${this.scoreEndTime}:00`).getTime() / 1000;
        const response = await this.axios.get(`/api/v1/starline/${this.car.id}/driving_score`, {
          params: { period_start, period_end },
          ...this.getAuthHeaders()
        });
        this.drivingScoreData = response.data.data || [];
        this.scoreLoaded = true;
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingScore = false;
      }
    },
    
    async fetchObdErrors() {
      this.loadingObdErrors = true;
      try {
        const response = await this.axios.get(`/api/v1/starline/${this.car.id}/obd_errors`, this.getAuthHeaders());
        this.obdErrors = response.data.obd_errors || [];
        this.obdErrorsChecked = true;
      } catch (error) {
        this.showError(error);
        this.obdErrorsChecked = true;
      } finally {
        this.loadingObdErrors = false;
      }
    },
    
    getObdErrorDescription(error) {
      if (!error.descriptions) return '-';
      return error.descriptions[this.locale] || error.descriptions.ru || error.descriptions.en || '-';
    },
    
    getWarningLevelColor(level) {
      if (level >= 8) return 'error';
      if (level >= 5) return 'warning';
      return 'info';
    },
    
    sendSelectedCommand() {
      if (!this.selectedCommand) return;
      this.executeCommand(this.selectedCommand.type, this.selectedCommand.value || 1);
    },
    
    async executeCommand(type, value = 1) {
      this.executingCommand = type;
      this.lastCommandType = type;
      try {
        const response = await this.axios.post(`/api/v1/starline/${this.car.id}/command`, {
          type,
          value
        }, this.getAuthHeaders());
        
        // Сохраняем cmd_id для отслеживания через WebSocket
        this.pendingCommandId = response.data.cmd_id;
        this.showInfo(this.$t('starline.command_sent'));
        
        // Таймаут на случай если WebSocket не отвечает
        this.commandTimeout = setTimeout(() => {
          if (this.executingCommand === type) {
            this.executingCommand = null;
            this.showError({ response: { data: { error: this.$t('starline.command_timeout') } } });
          }
        }, 60000); // 60 секунд таймаут
      } catch (error) {
        this.showError(error);
        this.executingCommand = null;
      }
    },
    
    showInfo(text) {
      this.snackbarText = text;
      this.snackbarColor = 'info';
      this.snackbar = true;
    },
    
    // Map functions
    openMap() {
      if (!this.position) return;
      const url = this.getMapUrl(this.position.lat, this.position.lon);
      window.open(url, '_blank');
    },
    
    openTrackOnMap() {
      if (!this.waysData?.way || this.waysData.way.length === 0) return;
      
      const wayPoints = this.waysData.way;
      
      // Формируем путь для карты
      this.trackPath = wayPoints.map(point => ({
        lat: point.lat,
        lng: point.lon || point.lng
      }));
      
      // Вычисляем центр и zoom
      if (this.trackPath.length > 0) {
        const lats = this.trackPath.map(p => p.lat);
        const lngs = this.trackPath.map(p => p.lng);
        const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
        const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
        this.trackCenter = { lat: centerLat, lng: centerLng };
        
        // Вычисляем zoom по размеру
        const latDiff = Math.max(...lats) - Math.min(...lats);
        const lngDiff = Math.max(...lngs) - Math.min(...lngs);
        const maxDiff = Math.max(latDiff, lngDiff);
        if (maxDiff > 0.5) this.trackZoom = 10;
        else if (maxDiff > 0.1) this.trackZoom = 12;
        else if (maxDiff > 0.01) this.trackZoom = 14;
        else this.trackZoom = 15;
      }
      
      // Добавляем события на трек (если есть загруженные)
      this.trackEvents = [];
      if (this.events && this.events.length > 0) {
        // Для каждого события ищем ближайшую точку трека по времени
        this.events.forEach(event => {
          if (event.ts && wayPoints.length > 0) {
            let closestPoint = null;
            let minTimeDiff = Infinity;
            
            wayPoints.forEach(point => {
              const timeDiff = Math.abs((point.ts || 0) - event.ts);
              if (timeDiff < minTimeDiff) {
                minTimeDiff = timeDiff;
                closestPoint = point;
              }
            });
            
            if (closestPoint && minTimeDiff < 3600) { // В пределах часа
              this.trackEvents.push({
                position: { lat: closestPoint.lat, lng: closestPoint.lon || closestPoint.lng },
                title: this.getEventTitle(event),
                time: this.formatDateTime(event.ts),
                text: event.message || event.event_type || '',
                type: event.event_type || 'info'
              });
            }
          }
        });
      }
      
      this.selectedEvent = null;
      this.trackMapDialog = true;
    },
    
    getEventTitle(event) {
      const type = event.event_type || '';
      if (type.includes('arm')) return this.$t('starline.events.arm');
      if (type.includes('disarm')) return this.$t('starline.events.disarm');
      if (type.includes('ign_start')) return this.$t('starline.events.ign_on');
      if (type.includes('ign_stop')) return this.$t('starline.events.ign_off');
      if (type.includes('alarm')) return this.$t('starline.events.alarm');
      return event.message || type || this.$t('starline.events.info');
    },
    
    getEventIcon(type) {
      const icons = {
        arm: { url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png' },
        disarm: { url: 'https://maps.google.com/mapfiles/ms/icons/yellow-dot.png' },
        ign_start: { url: 'https://maps.google.com/mapfiles/ms/icons/orange-dot.png' },
        ign_stop: { url: 'https://maps.google.com/mapfiles/ms/icons/purple-dot.png' },
        alarm: { url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png' }
      };
      
      for (const [key, icon] of Object.entries(icons)) {
        if (type && type.includes(key)) return icon;
      }
      return { url: 'https://maps.google.com/mapfiles/ms/icons/ltblue-dot.png' };
    },
    
    getMapUrl(lat, lon) {
      if (this.locale === 'ru') {
        return `https://yandex.ru/maps/?pt=${lon},${lat}&z=16&l=map`;
      }
      return `https://www.google.com/maps?q=${lat},${lon}`;
    },
    
    getTrackUrl(points) {
      if (!points || points.length === 0) return '';
      
      // Для Яндекс/Google маршрутов ограничиваем до 10 точек 
      // (иначе карты показывают слишком много промежуточных номеров)
      const maxPoints = 10;
      let sampledPoints = points;
      if (points.length > maxPoints) {
        const step = Math.ceil(points.length / (maxPoints - 2)); // -2 для начала и конца
        sampledPoints = [points[0]]; // Начальная точка
        for (let i = step; i < points.length - 1; i += step) {
          sampledPoints.push(points[i]);
        }
        sampledPoints.push(points[points.length - 1]); // Конечная точка
      }
      
      if (this.locale === 'ru') {
        // Яндекс Карты - строим маршрут от начала до конца через промежуточные точки
        const rtext = sampledPoints.map(p => `${p.lat},${p.lon || p.lng}`).join('~');
        return `https://yandex.ru/maps/?rtext=${rtext}&rtt=auto`;
      }
      // Google Maps
      if (sampledPoints.length < 2) return '';
      const start = `${sampledPoints[0].lat},${sampledPoints[0].lon || sampledPoints[0].lng}`;
      const end = `${sampledPoints[sampledPoints.length - 1].lat},${sampledPoints[sampledPoints.length - 1].lon || sampledPoints[sampledPoints.length - 1].lng}`;
      const waypoints = sampledPoints.slice(1, -1).map(p => `${p.lat},${p.lon || p.lng}`).join('|');
      if (waypoints) {
        return `https://www.google.com/maps/dir/${start}/${waypoints}/${end}`;
      }
      return `https://www.google.com/maps/dir/${start}/${end}`;
    },
    
    // Control helpers
    getCommandJson(command) {
      if (!command) return '';
      const type = command.type;
      
      // Формируем JSON команды для API /async
      // По документации StarLine все команды: {"type": "command_type", "value": 1}
      // type - полное имя команды (ign_start, arm_stop, general_out_1_on и т.д.)
      return JSON.stringify({ type: type, value: 1 });
    },
    
    getControlIcon(type) {
      const icons = {
        arm: 'mdi-shield-lock',
        arm_start: 'mdi-shield-lock',
        arm_stop: 'mdi-shield-off',
        arm_quiet: 'mdi-shield-lock-outline',
        arm_start_quiet: 'mdi-shield-lock-outline',
        arm_stop_quiet: 'mdi-shield-off-outline',
        ign: 'mdi-engine',
        ign_start: 'mdi-engine',
        ign_stop: 'mdi-engine-off',
        poke: 'mdi-bell-ring',
        panic: 'mdi-alarm-light',
        disarm_trunk: 'mdi-car-back',
        getbalance: 'mdi-currency-usd',
        update_position: 'mdi-crosshairs-gps',
        webasto: 'mdi-thermometer',
        webasto_on: 'mdi-thermometer-plus',
        webasto_off: 'mdi-thermometer-minus',
        valet: 'mdi-key-variant',
        hijack: 'mdi-lock',
        shock_bpass: 'mdi-vibrate-off',
        tilt_bpass: 'mdi-angle-acute',
        hfree: 'mdi-hand-back-right-off',
        out: 'mdi-power-plug',
        dvr: 'mdi-video',
        mhrs_reset: 'mdi-timer-refresh'
      };
      return icons[type] || 'mdi-cog';
    },
    
    getControlColor(type) {
      const state = this.carState || {};
      
      // Охрана: яркая кнопка = доступное действие
      if (type === 'arm_start') {
        // Если НЕ на охране → можно поставить → зелёная
        // Если на охране → уже сделано → серая
        return state.arm ? 'grey' : 'success';
      }
      if (type === 'arm_stop') {
        // Если на охране → можно снять → красная
        // Если НЕ на охране → уже снято → серая
        return state.arm ? 'error' : 'grey';
      }
      
      // Двигатель
      if (type === 'ign_start') {
        // Если выключен → можно запустить → жёлтая
        // Если работает → уже запущен → серая
        return (state.ign || state.run) ? 'grey' : 'warning';
      }
      if (type === 'ign_stop') {
        // Если работает → можно остановить → красная
        // Если выключен → уже остановлен → серая
        return (state.ign || state.run) ? 'error' : 'grey';
      }
      
      // Сервисный режим (toggle)
      if (type === 'valet') {
        return state.valet ? 'warning' : 'grey'; // Жёлтый если активен
      }
      
      // Webasto
      if (type === 'webasto_on') {
        return state.webasto ? 'grey' : 'warning';
      }
      if (type === 'webasto_off') {
        return state.webasto ? 'error' : 'grey';
      }
      
      // Обходы датчиков (toggle) - показывают текущее состояние
      if (type === 'shock_bpass') {
        return state.shock_bpass ? 'warning' : 'grey'; // Жёлтый если обход активен
      }
      if (type === 'tilt_bpass') {
        return state.tilt_bpass ? 'warning' : 'grey';
      }
      
      // Статичные цвета для остальных
      if (type === 'panic') return 'error';
      if (type === 'poke') return 'success';
      if (type === 'disarm_trunk') return 'primary';
      if (type === 'getbalance' || type === 'update_position') return 'info';
      if (type === 'mhrs_reset') return 'warning'; // Сброс моточасов - предупреждающий цвет
      
      return 'primary';
    },
    
    getControlLabel(control) {
      // Приоритет: title из API (controls_library)
      if (control.title) {
        return control.title;
      }
      
      const type = control.type || control;
      
      // Fallback: локализация
      const key = `starline.controls_list.${type}`;
      const translation = this.$t(key);
      if (translation !== key) return translation;
      
      // Fallback: имя типа с форматированием
      return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    
    getEventLabel(type) {
      const key = `starline.events.${type}`;
      const translation = this.$t(key);
      if (translation !== key) return translation;
      return type;
    },
    
    getScoreColor(value) {
      if (value >= 80) return 'success';
      if (value >= 60) return 'warning';
      return 'error';
    },
    
    getBatteryColor(voltage) {
      if (voltage >= 12.4) return 'success';
      if (voltage >= 11.8) return 'warning';
      return 'error';
    },
    
    getGsmColor(level) {
      if (level >= 50) return 'success';
      if (level >= 20) return 'warning';
      return 'error';
    },
    
    // ActionCable subscription (native; switch per car via held ref + same-id no-op)
    onStarlineConnected() {
      console.log("StarlineChannel connected");
    },
    onStarlineDisconnected() {
      console.log("StarlineChannel disconnected");
    },
    onStarlineRejected() {
      console.log("StarlineChannel rejected");
    },
    subscribeToChannel() {
      if (!this.car?.id) return;
      // Same-car no-op — already subscribed to this car's StarlineChannel
      if (this.starlineSub && this.subscribedCarId === this.car.id) return;
      // Switch: unsubscribe the previous car (held ref) before subscribing the new one
      if (this.starlineSub) {
        this.$cableUnsubscribe(this.starlineSub);
        this.starlineSub = null;
      }
      this.subscribedCarId = this.car.id;
      this.starlineSub = this.$cableSubscribe(
        { channel: 'StarlineChannel', room: this.subscribedCarId, car_id: this.subscribedCarId },
        {
          connected: () => this.onStarlineConnected(),
          disconnected: () => this.onStarlineDisconnected(),
          rejected: () => this.onStarlineRejected(),
          received: (data) => this.handleChannelMessage(data),
        },
      );
    },

    unsubscribeFromChannel() {
      if (this.starlineSub) {
        this.$cableUnsubscribe(this.starlineSub);
        this.starlineSub = null;
      }
      this.subscribedCarId = null;
    },

    handleChannelMessage(data) {
      console.log('[StarlineChannel] Received:', data);
      
      switch (data.event) {
        case 'command_started':
          // Команда начала выполняться
          this.showInfo(this.$t('starline.command_processing'));
          break;
        // Тела case обёрнуты в блоки: без них let/const из одного case живут в
        // общей области switch и в соседних ветках лежат в TDZ.
        case 'command_completed': {
          // Очищаем таймаут
          if (this.commandTimeout) {
            clearTimeout(this.commandTimeout);
            this.commandTimeout = null;
          }
          
          this.executingCommand = null;
          this.pendingCommandId = null;
          
          // Формируем сообщение с результатом
          let message = this.$t('starline.command_completed');
          if (data.command_type) {
            message = `${this.getControlLabel(data.command_type)}: ${this.$t('starline.command_completed')}`;
          }
          this.showSuccess(message);
          
          // Обновляем данные устройства
          this.fetchDeviceDetails();
          break;
        }
        case 'command_failed': {
          // Очищаем таймаут
          if (this.commandTimeout) {
            clearTimeout(this.commandTimeout);
            this.commandTimeout = null;
          }
          
          this.executingCommand = null;
          this.pendingCommandId = null;
          
          const errorMsg = data.error || this.$t('starline.command_error');
          this.showError({ response: { data: { error: errorMsg } } });
          break;
        }
        case 'state_update':
          this.deviceData = data.data;
          break;
        case 'position_update':
          this.position = data.data;
          this.positionUpdatedAt = data.data.ts;
          break;
      }
    },
    
    // Notifications
    showSuccess(message) {
      this.snackbarText = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },
    
    showError(error) {
      const errorCode = error?.response?.data?.error_code;
      const errorString = error?.response?.data?.error;
      
      // Получаем локализованное сообщение по коду ошибки
      let message;
      if (errorCode) {
        const key = `starline.errors.code_${errorCode}`;
        message = this.$t(key);
        // Если перевод не найден, используем ключ по умолчанию
        if (message === key) {
          message = this.$t('starline.errors.unknown', { code: errorCode, error: errorString });
        }
      } else if (errorString) {
        message = errorString;
      } else {
        message = this.$t('errors.something_went_wrong');
      }
      
      this.snackbarText = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
.v-chip {
  margin: 2px;
}
</style>
